import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingBag, MapPin } from 'lucide-react';
import { useCart } from '../context/CartContext';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { cartCount, setCartOpen } = useCart();
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location]);

  const navLinks = [
    { to: '/explore', label: 'Explore' },
    { to: '/packages', label: 'Packages' },
    { to: '/visa', label: 'Visa Services' },
    { to: '/car-rental', label: 'Car Rental' },
    { to: '/tickets', label: 'Tickets' },
    { to: '/airport-transfer', label: 'Transfers' },
    { to: '/about', label: 'About' },
  ];

  const bgClass = scrolled || !isHome
    ? 'bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-sm'
    : 'bg-transparent';
  const textClass = scrolled || !isHome ? 'text-navy' : 'text-white';
  const logoClass = scrolled || !isHome ? 'text-navy' : 'text-white';

  return (
    <nav data-testid="navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <MapPin className={`w-6 h-6 text-gold ${scrolled || !isHome ? '' : 'drop-shadow-lg'}`} />
            <span className={`font-heading text-lg md:text-xl font-bold tracking-tight ${logoClass} transition-colors`}>
              BEYOND <span className="text-gold">HORIZONS</span>
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-body font-medium tracking-wide hover:text-gold transition-colors ${textClass}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            <button
              data-testid="cart-button"
              onClick={() => setCartOpen(true)}
              className={`relative p-2 rounded-full hover:bg-white/10 transition-colors ${textClass}`}
            >
              <ShoppingBag className="w-5 h-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-gold text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
            <Link
              to="/contact"
              data-testid="book-now-btn"
              className="hidden sm:inline-flex bg-gold text-white hover:bg-gold-dark rounded-full px-6 py-2.5 text-sm font-body font-medium transition-all hover:shadow-[0_4px_14px_0_rgba(197,160,89,0.39)] hover:-translate-y-0.5"
            >
              Book Now
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className={`lg:hidden p-2 ${textClass}`}
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 animate-slide-down">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block px-4 py-3 rounded-xl text-navy font-body font-medium hover:bg-navy-50 transition-colors"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="block px-4 py-3 mt-2 bg-gold text-white text-center rounded-full font-body font-medium"
            >
              Book Now
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
